// src/hooks/useApplicationActions.js
import { useState, useCallback } from 'react';
import { clabAPI } from '../utils/api';

/**
 * Custom hook for managing application actions (approve/reject)
 * @param {Function} onSuccess - Callback function on successful action
 * @param {Function} onError - Callback function on error
 * @returns {Object} Hook state and methods
 */
export const useApplicationActions = (onSuccess, onError) => {
  const [processing, setProcessing] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [rejectionForm, setRejectionForm] = useState({
    reason_id: "",
    remarks: "",
  });

  // Reset form data
  const resetForm = useCallback(() => {
    setRejectionForm({
      reason_id: "",
      remarks: "",
    });
  }, []);

  // Handle approve action
  const handleApprove = useCallback((application) => {
    setSelectedApplication(application);
    setShowApproveModal(true);
  }, []);

  // Handle reject action
  const handleReject = useCallback((application) => {
    setSelectedApplication(application);
    setShowRejectModal(true);
  }, []);

  // Confirm approve with error handling
  const confirmApprove = useCallback(async () => {
    if (!selectedApplication) {
      onError?.("No application selected");
      return;
    }
    
    setProcessing(true);
    try {
      await clabAPI.approveApplication(selectedApplication.id);
      onSuccess?.("Application approved successfully");
      
      // Reset modal state
      setShowApproveModal(false);
      setSelectedApplication(null);
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to approve application";
      onError?.(errorMessage);
    } finally {
      setProcessing(false);
    }
  }, [selectedApplication, onSuccess, onError]);

  // Confirm reject with validation and error handling
  const confirmReject = useCallback(async () => {
    if (!selectedApplication) {
      onError?.("No application selected");
      return;
    }
    
    if (!rejectionForm.reason_id) {
      onError?.("Please select a rejection reason");
      return;
    }
    
    setProcessing(true);
    try {
      await clabAPI.rejectApplication(selectedApplication.id, rejectionForm);
      onSuccess?.("Application rejected successfully");
      
      // Reset modal state and form
      setShowRejectModal(false);
      setSelectedApplication(null);
      resetForm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to reject application";
      onError?.(errorMessage);
    } finally {
      setProcessing(false);
    }
  }, [selectedApplication, rejectionForm, onSuccess, onError, resetForm]);

  // Cancel approve modal
  const cancelApprove = useCallback(() => {
    setShowApproveModal(false);
    setSelectedApplication(null);
  }, []);

  // Cancel reject modal
  const cancelReject = useCallback(() => {
    setShowRejectModal(false);
    setSelectedApplication(null);
    resetForm();
  }, [resetForm]);

  // Update rejection form
  const updateRejectionForm = useCallback((updates) => {
    setRejectionForm(prev => ({ ...prev, ...updates }));
  }, []);

  return {
    // State
    processing,
    showApproveModal,
    showRejectModal,
    selectedApplication,
    rejectionForm,
    
    // Actions
    handleApprove,
    handleReject,
    confirmApprove,
    confirmReject,
    cancelApprove,
    cancelReject,
    updateRejectionForm,
    resetForm,
  };
};

export default useApplicationActions;
